import Link from 'next/link';

import Icon from '@/components/ui/AppIcon';
import CloudinaryImage from '@/components/ui/CloudinaryImage';

interface Equipment {
  id: string;
  title: string;
  price: string;
  location: string;
  image: string;
  alt: string;
  seller: string;
  year: number;
  hours: string;
  condition: string;
  whatsappNumber: string;
}

interface MarketplaceSectionProps {
  equipment: Equipment[];
}

const MarketplaceSection = ({ equipment }: MarketplaceSectionProps) => {
  // Helper function to format WhatsApp link
  const formatWhatsAppLink = (phoneNumber: string, itemTitle: string): string => {
    const cleanNumber = phoneNumber?.replace(/\D/g, '') || '';
    const message = encodeURIComponent(`Hi, I'm interested in: ${itemTitle}`);
    return `https://wa.me/${cleanNumber}?text=${message}`;
  };

  return (
    <section className="py-12 bg-muted">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              Featured Equipment
            </h2>
            <p className="text-text-secondary">
              Browse the latest mining equipment and machinery for sale
            </p>
          </div>
          <Link href="/marketplace-listings">
            <button className="hidden md:flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-industrial hover:bg-accent transition-industrial font-heading font-bold uppercase tracking-wide">
              <span>View Marketplace</span>
              <Icon name="ArrowRightIcon" size={20} />
            </button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {equipment?.map((item) => (
            <div
              key={item.id}
              className="bg-card border border-border rounded-industrial overflow-hidden industrial-shadow hover:shadow-lg transition-industrial"
            >
              <Link href={`/equipment-detail?id=${item.id}`}>
                <div className="relative h-48 overflow-hidden bg-muted">
                  <CloudinaryImage
                    src={item.image || ''}
                    alt={item.alt || item.title || 'Equipment image'}
                    fill
                    preset="card"
                    responsive
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    className="object-cover"
                  />
                </div>

                <div className="p-6">
                  <h3 className="text-lg font-heading font-bold text-card-foreground mb-2 line-clamp-2 hover:text-primary transition-industrial">
                    {item.title}
                  </h3>
                  
                  <div className="text-2xl font-heading font-bold text-primary mb-3">
                    {item.price}
                  </div>
                  
                  <div className="space-y-2 text-sm text-text-secondary mb-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="MapPinIcon" size={14} />
                      <span>{item.location}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="CalendarIcon" size={14} />
                      <span>{item.year} • {item.hours}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="UserIcon" size={14} />
                      <span>{item.seller}</span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-3">
                    <Link
                      href={`/equipment-detail?id=${item.id}`}
                      className="flex-1 bg-secondary text-secondary-foreground px-4 py-2 rounded text-center font-heading font-bold uppercase tracking-wide text-sm hover:bg-secondary/90 transition-industrial"
                    >
                      View Details
                    </Link>
                    {item.whatsappNumber && (
                      <a
                        href={formatWhatsAppLink(item.whatsappNumber, item.title)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-green-600 text-white px-4 py-2 rounded flex items-center justify-center hover:bg-green-700 transition-industrial"
                      >
                        <Icon name="ChatBubbleLeftRightIcon" size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Mobile View Marketplace Button */}
        <div className="mt-8 md:hidden">
          <Link href="/marketplace-listings">
            <button className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-industrial hover:bg-accent transition-industrial font-heading font-bold uppercase tracking-wide">
              <span>View Marketplace</span>
              <Icon name="ArrowRightIcon" size={20} />
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default MarketplaceSection;